import React, {
  useMemo,
  useRef,
  useState,
  useContext,
  useEffect,
  Context,
  ComponentType,
  ReactNode,
} from 'react';

import {
  initialize,
  LDClient,
  LDContext,
  LDOptions,
} from 'launchdarkly-js-client-sdk';

import { getRegion } from '@adsk/acs-sc-web-platform-api';

import { useCurrentAccountId } from './CurrentAccountIdContext';
import { useCurrentProjectId } from './CurrentProjectIdContext';
import { useUser } from './ProjectEntitlementsContext';

export type LaunchDarklyContextValue = {
  client: {
    current: LDClient | null;
  };
  loading: boolean;
};

type SimpleLaunchDarklyProviderProps = {
  apiKey: string;
  user?: LDContext;
  options?: LDOptions;
  children?: ReactNode;
};

export type LaunchDarklyProviderProps = {
  apiKey: string;
  projectEntitlementsUnavailable?: boolean;
  children?: ReactNode;
  customUserAttrs?: LDContext['custom'];
  options?: LDOptions;
};

export type UseFeatureFlagOptions = {
  liveStreaming: boolean;
};

export const DEFAULT_ANONYMOUS_USER_ID = 'nonuser' as const;

const defaultOptions = {
  allAttributesPrivate: true,
  sendEventsOnlyForVariation: true,
  streaming: true,
};

/**
 * This method creates a new LaunchDarkly providers & hooks.
 * We need this method in order to have multiple contexts in a single page (one for Web Platform, one for RFIs, etc.)
 * That's the reason for creating `LaunchDarklyContext` inside this method (or using the provided one), instead of globally.
 * See example for how we generate this in ./AlloyLaunchDarklyProvider.js
 *
 * @returns {{ SimpleLaunchDarklyProvider, LaunchDarklyProvider, useFeatureFlag, useAllFeatureFlags, }}
 */
const createLaunchDarklyContext = (
  context?: Context<LaunchDarklyContextValue | undefined>,
) => {
  const LaunchDarklyContext =
    context ||
    React.createContext<LaunchDarklyContextValue | undefined>(undefined);

  /**
   * Simple Launch Darkly provider without expecting any other provider to exist.
   *
   * @param apiKey
   * @param user
   * @param options
   * @param children
   * @constructor
   */
  const SimpleLaunchDarklyProvider: ComponentType<
    SimpleLaunchDarklyProviderProps
  > = ({ apiKey, user, options, children }) => {
    const client = useRef<LDClient | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      let cancel = false;
      const endLoadingIFNeeded = () => {
        if (!cancel) {
          setLoading(false);
        }
      };
      if (user) {
        if (client.current === null) {
          client.current = initialize(apiKey, user, options);
          client.current.on('ready', endLoadingIFNeeded);
        } else {
          if (!cancel) {
            setLoading(true);
            client.current.identify(user, undefined, endLoadingIFNeeded);
          }
        }
      }
      return () => {
        cancel = true;
      };
    }, [apiKey, user, options]);

    const memoizedValue = useMemo(
      () => ({ loading, client }),
      [loading, client],
    );
    return (
      <LaunchDarklyContext.Provider value={memoizedValue}>
        {children}
      </LaunchDarklyContext.Provider>
    );
  };

  /**
   * Launch Darkly Provider that expected to be inside `AlloyProvider` and uses this data to initiate LD calls.
   *
   * @param apiKey
   * @param projectEntitlementsUnavailable
   * @param children
   * @param customUserAttrs
   * @constructor
   */
  const LaunchDarklyProvider: ComponentType<LaunchDarklyProviderProps> = ({
    apiKey,
    projectEntitlementsUnavailable = false,
    children,
    customUserAttrs,
    options = defaultOptions,
  }) => {
    const projectId = useCurrentProjectId();
    const accountId = useCurrentAccountId();
    const { user } = useUser();
    const { analyticsId, email } = user || {};
    const region = getRegion();
    const dataReady =
      region &&
      user &&
      (accountId || projectId || projectEntitlementsUnavailable);
    const ldUser = useMemo(() => {
      if (!dataReady) {
        return undefined;
      }
      const custom = customUserAttrs ?? {};
      if (region) {
        Object.assign(custom, { region });
      }
      if (accountId) {
        Object.assign(custom, { accountId });
      }
      if (projectId) {
        Object.assign(custom, { projectId });
      }
      return {
        kind: 'user',
        key: analyticsId || DEFAULT_ANONYMOUS_USER_ID,
        anonymous: true,
        email,
        ...custom,
      };
    }, [
      dataReady,
      customUserAttrs,
      region,
      accountId,
      projectId,
      analyticsId,
      email,
    ]);

    return (
      <SimpleLaunchDarklyProvider
        user={ldUser}
        apiKey={apiKey}
        options={options}
      >
        {children}
      </SimpleLaunchDarklyProvider>
    );
  };

  /**
   *
   * @param flagName
   * @param defaultValue
   * @returns {{loading, value: *}}
   */
  const useFeatureFlag = <FlagValue,>(
    flagName: string,
    defaultValue?: FlagValue,
  ) => {
    const ldContext = useContext(LaunchDarklyContext);
    if (!ldContext) {
      return {
        loading: false,
        value: defaultValue,
      };
    }
    const { loading, client } = ldContext;
    const value = loading
      ? defaultValue
      : client.current?.variation(flagName, defaultValue);
    return { loading, value };
  };

  /**
   * Ensures stable feature flag value and loading state without flickering during additional fetches.
   * @param flagName - The name of the feature flag to retrieve
   * @param defaultValue - The default value to use before the flag is loaded
   * @returns Object containing stable loading state and flag value
   */
  const useFeatureFlagOnce = <FlagValue,>(
    flagName: string,
    defaultValue?: FlagValue,
  ) => {
    const { value, loading } = useFeatureFlag(flagName, defaultValue);
    const [stableLoading, setStableLoading] = useState<boolean>(true);
    const [stableValue, setStableValue] = useState<FlagValue | undefined>(
      defaultValue,
    );

    useEffect(() => {
      if (!loading && stableLoading === true) {
        setStableLoading(false);
      }
    }, [loading, stableLoading]);

    useEffect(() => {
      if (!loading && value !== stableValue) {
        setStableValue(value);
      }
    }, [loading, stableValue, value]);

    return {
      value: stableValue,
      loading: stableLoading,
    };
  };

  /**
   *
   * @param flagName
   * @param defaultValue
   * @returns {{loading, value: *}}
   */

  const useLiveFeatureFlag = <FlagValue,>(
    flagName: string,
    defaultValue?: FlagValue,
  ) => {
    const ldContext = useContext(LaunchDarklyContext);
    const [value, setValue] = useState<FlagValue | undefined>();
    const isInitialized = useRef(false);
    const { client, loading: ldLoading } = ldContext || {};

    const finalValue = useMemo(
      () => value ?? defaultValue,
      [defaultValue, value],
    );

    const loading: boolean = ldLoading || !isInitialized.current;

    useEffect(() => {
      if (!client?.current) return;

      if (!ldLoading && client?.current && !isInitialized.current) {
        client.current.on(`change:${flagName}`, (updatedValue) => {
          setValue(updatedValue);
        });

        setValue(client.current.variation(flagName, defaultValue));
        isInitialized.current = true;
      }
    }, [client, defaultValue, flagName, ldLoading, value]);

    return {
      loading: ldContext ? loading : false,
      value: finalValue,
    };
  };

  /**
   *
   * @returns {{loading, value: *}}
   */
  const useAllFeatureFlags = () => {
    const ldContext = useContext(LaunchDarklyContext);
    if (!ldContext) {
      return {
        loading: false,
        value: {},
      };
    }
    const { loading, client } = ldContext;
    const value = loading ? {} : client.current?.allFlags();
    return { loading, value };
  };

  return {
    SimpleLaunchDarklyProvider,
    LaunchDarklyProvider,
    useFeatureFlag,
    useFeatureFlagOnce,
    useLiveFeatureFlag,
    useAllFeatureFlags,
  };
};

export default createLaunchDarklyContext;
